import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle, Download } from 'lucide-react';
import SEO from '../../components/SEO';
import SeoNav from '../../components/SeoNav';

const schema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: 'What should a self-employed invoice include?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'A self-employed invoice should include: your full name or trading name; your address and contact details; the client name and address; a unique invoice number; the invoice date and payment due date; a clear description of the work done; the amount charged (with hourly rate or flat fee); and your payment details (bank account, PayPal, or other method). If you are VAT-registered, also include your VAT number and the tax breakdown.',
      },
    },
    {
      '@type': 'Question',
      name: 'Do self-employed people need to send invoices?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Yes. As a self-employed person or sole trader, sending invoices is how you formally request payment from clients. Invoices also serve as financial records for tax purposes. Most tax authorities (HMRC, IRS, ATO, etc.) require you to keep records of your income and the invoices that generated it.',
      },
    },
    {
      '@type': 'Question',
      name: 'Is there a free invoice template for self-employed workers?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Yes. Invilic provides a free invoice template for self-employed workers, freelancers, and sole traders. Fill in your details online and download a professional PDF in seconds. No Word or Excel file needed. Free for up to 5 invoices per month with no sign-up.',
      },
    },
    {
      '@type': 'Question',
      name: 'How do I number invoices as a self-employed person?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Use a simple sequential numbering system, for example INV-001, INV-002, or 2025-001. Never reuse an invoice number. Consistent numbering makes it easier to track outstanding payments and present records to an accountant or tax authority.',
      },
    },
    {
      '@type': 'Question',
      name: 'What payment terms should self-employed people use?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Common payment terms are "Due on receipt" (pay immediately), "Net 14" (14 days), or "Net 30" (30 days). For new clients, shorter terms like Net 14 reduce the risk of late payment. Always state the due date clearly on the invoice rather than just writing "30 days" so there is no ambiguity.',
      },
    },
  ],
};

export default function SelfEmployedInvoiceTemplatePage() {
  return (
    <div className="min-h-screen bg-white dark:bg-gray-950">
      <SEO
        title="Self-Employed Invoice Template: Free PDF for Sole Traders | Invilic"
        description="Free invoice template for self-employed workers and sole traders. Bill clients professionally, download PDF instantly. No sign-up required. Works worldwide."
        canonical="https://www.invilic.com/self-employed-invoice-template"
        schema={schema}
      />

      <SeoNav />

      <main className="max-w-4xl mx-auto px-6 py-16">
        {/* Hero */}
        <div className="text-center mb-16">
          <span className="inline-block bg-teal-50 dark:bg-teal-900/30 text-teal-700 dark:text-teal-300 text-xs font-semibold px-3 py-1 rounded-full uppercase tracking-wider mb-4">
            Free Template
          </span>
          <h1 className="text-4xl sm:text-5xl font-extrabold text-gray-900 dark:text-white leading-tight mb-5">
            Self-Employed Invoice Template
          </h1>
          <p className="text-xl text-gray-500 dark:text-gray-400 max-w-2xl mx-auto leading-relaxed mb-8">
            A free invoice template for self-employed people, sole traders, and independent contractors. Fill in your details online and download a professional PDF in minutes. No Word files, no Excel formulas, no sign-up.
          </p>
          <Link
            to="/create/freelancer"
            className="inline-flex items-center gap-2 bg-teal-600 hover:bg-teal-700 text-white font-semibold px-8 py-4 rounded-xl shadow-lg shadow-teal-600/20 transition-all duration-200 text-lg"
          >
            Use Free Template <ArrowRight className="w-5 h-5" />
          </Link>
          <p className="text-sm text-gray-400 dark:text-gray-500 mt-3">Free · No sign-up · PDF in seconds</p>
        </div>

        {/* Template fields */}
        <section className="mb-16">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-8 text-center">
            What's in the Self-Employed Invoice Template
          </h2>
          <div className="grid sm:grid-cols-2 gap-3">
            {[
              'Your name or trading name & logo',
              'Your address and contact details',
              'Client name and address',
              'Invoice number (auto-incremented)',
              'Invoice date and payment due date',
              'Description of work per line item',
              'Hourly rate or flat fee with quantity',
              'Subtotal, tax and discount',
              'Total amount due',
              'Bank details or payment link',
              'Payment terms and notes',
              'Optional: VAT number (if VAT-registered)',
            ].map(f => (
              <div key={f} className="flex items-center gap-3 p-4 rounded-xl border border-teal-100 dark:border-teal-800/30 bg-teal-50 dark:bg-teal-900/10">
                <CheckCircle className="w-4 h-4 text-teal-600 dark:text-teal-400 flex-shrink-0" />
                <span className="text-sm text-gray-700 dark:text-gray-300">{f}</span>
              </div>
            ))}
          </div>
        </section>

        {/* Steps */}
        <section className="mb-16">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-8 text-center">
            Create Your Self-Employed Invoice in 3 Steps
          </h2>
          <div className="grid sm:grid-cols-3 gap-6">
            {[
              { n: '1', title: 'Open the template', desc: 'No download or account needed. Open instantly in your browser.' },
              { n: '2', title: 'Fill in your details', desc: 'Add your info, your client, your work description, and rate. Takes 2 minutes.' },
              { n: '3', title: 'Download PDF', desc: 'Get a professional, branded PDF invoice ready to send.' },
            ].map(({ n, title, desc }) => (
              <div key={n} className="text-center p-6 rounded-2xl border border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-900/50">
                <div className="w-10 h-10 rounded-full bg-teal-600 text-white font-bold text-lg flex items-center justify-center mx-auto mb-4">
                  {n}
                </div>
                <h3 className="font-semibold text-gray-900 dark:text-white mb-2">{title}</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">{desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* VAT callout */}
        <section className="mb-16 p-6 rounded-2xl border border-orange-100 dark:border-orange-800/40 bg-orange-50 dark:bg-orange-900/20">
          <h2 className="font-bold text-gray-900 dark:text-white mb-2">VAT-Registered? Use the VAT Invoice Template Instead</h2>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
            If your annual turnover exceeds the VAT registration threshold in your country (£90,000 in the UK, 10% GST in Australia at AUD $75,000), you must issue VAT/GST invoices showing your registration number and the tax amount separately.
          </p>
          <Link
            to="/vat-invoice-template"
            className="inline-flex items-center gap-2 text-sm font-semibold text-orange-700 dark:text-orange-400 hover:underline"
          >
            Use VAT Invoice Template <ArrowRight className="w-4 h-4" />
          </Link>
        </section>

        {/* Tips */}
        <section className="mb-16">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
            Tips for Getting Paid Faster
          </h2>
          <div className="space-y-3">
            {[
              'Send the invoice the same day you complete the work.',
              'Always include a specific due date, not just "30 days".',
              'Add your bank account or payment link directly on the invoice.',
              'Use sequential invoice numbers for easy record-keeping.',
              'Follow up politely with a reminder 2 days before the due date.',
            ].map(tip => (
              <div key={tip} className="flex items-start gap-3 p-4 rounded-xl border border-gray-100 dark:border-gray-800">
                <CheckCircle className="w-4 h-4 text-teal-500 flex-shrink-0 mt-0.5" />
                <span className="text-sm text-gray-700 dark:text-gray-300">{tip}</span>
              </div>
            ))}
          </div>
        </section>

        {/* FAQ */}
        <section className="mb-16">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-8">
            Self-Employed Invoice Template: FAQs
          </h2>
          <div className="space-y-4">
            {schema.mainEntity.map(({ name, acceptedAnswer }) => (
              <details key={name} className="group border border-gray-100 dark:border-gray-800 rounded-xl overflow-hidden">
                <summary className="flex items-center justify-between px-6 py-4 cursor-pointer font-medium text-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-900/50 list-none">
                  {name}
                  <ArrowRight className="w-4 h-4 text-gray-400 transition-transform group-open:rotate-90 flex-shrink-0 ml-4" />
                </summary>
                <p className="px-6 pb-5 text-gray-500 dark:text-gray-400 text-sm leading-relaxed">
                  {acceptedAnswer.text}
                </p>
              </details>
            ))}
          </div>
        </section>

        {/* CTA */}
        <section className="text-center py-12 border-t border-gray-100 dark:border-gray-800">
          <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
            Get Your Free Self-Employed Invoice Template
          </h2>
          <p className="text-gray-500 dark:text-gray-400 mb-8">Professional PDF. No account required. Works worldwide.</p>
          <Link
            to="/create/freelancer"
            className="inline-flex items-center gap-2 bg-teal-600 hover:bg-teal-700 text-white font-semibold px-8 py-4 rounded-xl shadow-lg shadow-teal-600/20 transition-all duration-200 text-lg"
          >
            <Download className="w-5 h-5" /> Use Invoice Template Free
          </Link>
        </section>
      </main>
    </div>
  );
}
